import { isNil } from "lodash-es";
import { EnterItemProps2ValueEntry } from "../../../../clients/graphqlTypes.js";
import {
  Props2AllFragment,
  Props2NonComputedAllFragment,
} from "../../doc-items/properties.generated.js";
import {
  getAllowedValues,
  getPropertyUnit,
  getPropValue,
  isNonComputedProp,
  isValueNumberInNumberRange,
  isValueNumberInNumberRangeStep,
  nonComputedPropToEntry,
  setPropValue,
} from "../../doc-items/properties.js";

/**
 * What the save-as-template dialog's Properties tab lists, and with which values.
 *
 * The tab describes the template about to be created, not the quote item it is
 * created from. Those differ whenever a template type is inherited: a template
 * type placeholder adds no properties to the item it sits on, so an item that
 * will produce a template with the type's full property set carries none of them
 * itself. Listing the item's `props2` therefore reported "no properties" for a
 * template created with several — and offered no way to fill them.
 *
 * Every value listed here is submitted with the save (`saveTemplatePropertyEntries`),
 * so the created template carries exactly what was on screen.
 */
export const deriveSaveTemplateProperties = ({
  templateTypeProps2,
  itemProps2,
}: {
  templateTypeProps2: readonly Props2NonComputedAllFragment[] | undefined;
  itemProps2: readonly Props2AllFragment[];
}): readonly Props2AllFragment[] => {
  // No inherited type — or one that defines nothing — leaves the item's own
  // properties as the template's, which is what they are. Returned as-is so the
  // tab's memo holds across the form's re-renders.
  if (!templateTypeProps2?.length) return itemProps2;

  const definedByType = new Set(templateTypeProps2.map(({ key }) => key));

  return [
    // The type's order, which is also the order the server imposes when it
    // applies a type — so the tab does not disagree with the saved template.
    ...templateTypeProps2.map(typeProperty =>
      prefillFromItem(typeProperty, itemProps2)
    ),
    // On the created template regardless: its root item is a clone of the source
    // item, whose properties the clone keeps. Omitting them would understate the
    // template — and its badge.
    ...itemProps2.filter(({ key }) => !definedByType.has(key)),
  ];
};

/** One entry per non-computed property, in the order the tab lists them. */
export const saveTemplatePropertyEntries = (
  properties: readonly Props2AllFragment[]
): EnterItemProps2ValueEntry[] =>
  properties.filter(isNonComputedProp).map(nonComputedPropToEntry);

/**
 * The type's property, carrying the item's value for the same key when the item
 * has one the type's definition accepts.
 *
 * Not the server's merge predicate (`aggregate_adjustProps2ForTemplateType`),
 * which also demands the item's own constraints be no narrower than the type's —
 * that decides whether the two *definitions* merge. Here the type's definition is
 * what is shown and submitted, so the only question is whether the value is legal
 * for it, which is also what the server validates on write.
 */
const prefillFromItem = (
  typeProperty: Props2NonComputedAllFragment,
  itemProps2: readonly Props2AllFragment[]
): Props2AllFragment => {
  const itemProperty = itemProps2.find(({ key }) => key === typeProperty.key);
  if (!itemProperty) return typeProperty;

  // A computed property's value comes from its expression; adopting the
  // evaluated result would freeze a derived value onto the template as a plain
  // one. It is also a different kind, which the server would refuse to merge.
  if (!isNonComputedProp(itemProperty)) return typeProperty;
  if (itemProperty.__typename !== typeProperty.__typename) return typeProperty;

  // The one condition this shares with the server's merge predicate, and for a
  // different reason: a number's unit is part of what its value *means*, so 1700
  // in a property measured in mm is not the value of a property measured in cm.
  // Legality would not catch it — the number can sit well inside the type's
  // range — and since the value is submitted verbatim, the template would state
  // a length nobody entered.
  if (getPropertyUnit(typeProperty) !== getPropertyUnit(itemProperty)) {
    return typeProperty;
  }

  const value = getPropValue(itemProperty);
  if (!isValueForTemplateTypeProperty(typeProperty, value)) return typeProperty;

  return setPropValue(typeProperty, value);
};

/**
 * Whether `value` is legal for the template type's property — checked per
 * element for the array kinds, because the server validates every element and a
 * partially legal array must not be prefilled at all.
 *
 * Composed from the property helpers rather than restating their rules; the
 * range/step pairing is the one `validateNumberValue` uses (a step subsumes the
 * plain range check).
 */
const isValueForTemplateTypeProperty = (
  typeProperty: Props2NonComputedAllFragment,
  value: unknown
): boolean => {
  switch (typeProperty.__typename) {
    case "Props2Bool":
      return typeof value === "boolean";
    case "Props2Text":
      return (
        typeof value === "string" &&
        value.length > 0 &&
        isAllowedValue(typeProperty, value)
      );
    case "Props2TextArray":
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(
          entry =>
            typeof entry === "string" && isAllowedValue(typeProperty, entry)
        )
      );
    case "Props2Number":
      return (
        typeof value === "number" &&
        isAllowedValue(typeProperty, value) &&
        isInRange(typeProperty, value)
      );
    case "Props2NumberArray":
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every(
          entry =>
            typeof entry === "number" &&
            isAllowedValue(typeProperty, entry) &&
            isInRange(typeProperty, entry)
        )
      );
  }
};

/** A property that enumerates nothing accepts any value of its kind. */
const isAllowedValue = (
  typeProperty: Props2NonComputedAllFragment,
  value: string | number
): boolean => {
  const allowedValues = getAllowedValues(typeProperty);
  return (
    allowedValues.length === 0 ||
    allowedValues.some(allowed => allowed.value === value)
  );
};

/**
 * A step subsumes the plain range check — the pairing `validateNumberValue` uses.
 * A property with no range constrains nothing.
 */
const isInRange = (
  typeProperty: Extract<
    Props2NonComputedAllFragment,
    { __typename: "Props2Number" | "Props2NumberArray" }
  >,
  value: number
): boolean => {
  const { range, step } = typeProperty;
  if (!range || (isNil(range.min) && isNil(range.max))) return true;

  return isNil(step)
    ? isValueNumberInNumberRange(value, range)
    : isValueNumberInNumberRangeStep(value, range, step);
};
